import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

// Token-based auth (primary). Cookies are a bonus but get blocked in iframes /
// cross-site (SameSite=None third-party), so we persist tokens in localStorage
// and send them as Authorization: Bearer.
let authToken = localStorage.getItem("sg_token") || null;
let refreshTok = localStorage.getItem("sg_refresh") || null;

export const setAuthToken = (t) => {
  authToken = t;
  if (t) localStorage.setItem("sg_token", t);
  else localStorage.removeItem("sg_token");
};
export const setRefreshToken = (t) => {
  refreshTok = t;
  if (t) localStorage.setItem("sg_refresh", t);
  else localStorage.removeItem("sg_refresh");
};
export const getAuthToken = () => authToken;

// Attach bearer token + the true page origin (for WebAuthn RP ID) to every request.
api.interceptors.request.use((config) => {
  if (authToken) config.headers.Authorization = `Bearer ${authToken}`;
  if (typeof window !== "undefined" && window.location) config.headers["X-Page-Origin"] = window.location.origin;
  return config;
});

// Build an authenticated file URL for <img> tags (query-param fallback auth).
export const fileUrl = (path) =>
  `${API}/files/${path}${authToken ? `?auth=${authToken}` : ""}`;

// Global 401 handling: one refresh attempt (body-based, cookie-independent), else login.
const SKIP_REFRESH = ["/auth/login", "/auth/refresh", "/auth/logout"];
let redirecting = false;
let refreshPromise = null; // single-flight: dedupe concurrent 401 refreshes

async function doRefresh() {
  if (!refreshPromise) {
    refreshPromise = api
      .post("/auth/refresh", { refresh_token: refreshTok })
      .then(({ data }) => { if (data?.token) setAuthToken(data.token); return data; })
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const status = error.response?.status;
    const cfg = error.config || {};
    const url = cfg.url || "";
    const skip = SKIP_REFRESH.some((p) => url.includes(p));
    if (status === 401 && !skip) {
      if (!cfg._retry && refreshTok) {
        cfg._retry = true;
        try {
          await doRefresh();
          return api(cfg);
        } catch (_) { /* refresh failed */ }
      }
      // Never hijack public auth pages (reset/forgot/login) — the anonymous /auth/me
      // check on load returns 401 by design and must not bounce the user off these pages.
      const PUBLIC_PREFIXES = ["/login", "/forgot-password", "/reset-password"];
      const onPublic = PUBLIC_PREFIXES.some((p) => window.location.pathname.startsWith(p));
      if (!redirecting && !onPublic) {
        redirecting = true;
        setAuthToken(null);
        setRefreshToken(null);
        window.location.assign("/login");
      }
    }
    return Promise.reject(error);
  }
);

export function apiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
